// =============================================================================
// ESPOLIDAR WEB ADMIN - ZONE FORM
// =============================================================================

import { useState, useEffect } from 'react'
import type { Zone, ZoneCreate } from '../types'

interface ZoneFormProps {
  zone?: Zone | null
  onSave: (data: ZoneCreate) => void
  onCancel: () => void
  nextZoneNumber?: number
}

export default function ZoneForm({ zone, onSave, onCancel, nextZoneNumber = 1 }: ZoneFormProps) {
  const [formData, setFormData] = useState<ZoneCreate>({
    zone_number: zone?.zone_number || nextZoneNumber,
    name: zone?.name || `Zona ${nextZoneNumber}`,
    enabled: zone?.enabled ?? true,
    angle_min: zone?.angle_min || 0,
    angle_max: zone?.angle_max || 90,
    distance_min: zone?.distance_min || 500,
    distance_max: zone?.distance_max || 3000,
    led_start: zone?.led_start || 0,
    led_end: zone?.led_end || 0,
    led_color: zone?.led_color || [255, 255, 255],
  })

  useEffect(() => {
    if (zone) {
      setFormData({
        zone_number: zone.zone_number,
        name: zone.name,
        enabled: zone.enabled,
        angle_min: zone.angle_min,
        angle_max: zone.angle_max,
        distance_min: zone.distance_min,
        distance_max: zone.distance_max,
        led_start: zone.led_start,
        led_end: zone.led_end,
        led_color: zone.led_color,
      })
    }
  }, [zone])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
  }

  const colorToHex = (rgb: [number, number, number]) => {
    return '#' + rgb.map(c => c.toString(16).padStart(2, '0')).join('')
  }

  const hexToColor = (hex: string): [number, number, number] => {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return [r, g, b]
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Nome */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome Zona
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="input"
            required
          />
        </div>

        {/* Numero zona */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Numero Zona
          </label>
          <input
            type="number"
            value={formData.zone_number}
            onChange={(e) => setFormData({ ...formData, zone_number: parseInt(e.target.value) })}
            className="input"
            min={1}
            required
            disabled={!!zone}
          />
        </div>

        {/* Abilitata */}
        <div className="flex items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">Zona Abilitata</span>
          </label>
        </div>
      </div>

      {/* Angoli */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Angoli (gradi)</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Angolo Min</label>
            <input
              type="number"
              value={formData.angle_min}
              onChange={(e) => setFormData({ ...formData, angle_min: parseFloat(e.target.value) })}
              className="input"
              min={0}
              max={360}
              step={1}
              required
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Angolo Max</label>
            <input
              type="number"
              value={formData.angle_max}
              onChange={(e) => setFormData({ ...formData, angle_max: parseFloat(e.target.value) })}
              className="input"
              min={0}
              max={360}
              step={1}
              required
            />
          </div>
        </div>
      </div>

      {/* Distanze */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Distanze (mm)</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Distanza Min</label>
            <input
              type="number"
              value={formData.distance_min}
              onChange={(e) => setFormData({ ...formData, distance_min: parseFloat(e.target.value) })}
              className="input"
              min={0}
              step={100}
              required
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Distanza Max</label>
            <input
              type="number"
              value={formData.distance_max}
              onChange={(e) => setFormData({ ...formData, distance_max: parseFloat(e.target.value) })}
              className="input"
              min={100}
              step={100}
              required
            />
          </div>
        </div>
      </div>

      {/* LED */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">LED WS2812B</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">LED Start</label>
            <input
              type="number"
              value={formData.led_start}
              onChange={(e) => setFormData({ ...formData, led_start: parseInt(e.target.value) })}
              className="input"
              min={0}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">LED End</label>
            <input
              type="number"
              value={formData.led_end}
              onChange={(e) => setFormData({ ...formData, led_end: parseInt(e.target.value) })}
              className="input"
              min={0}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Colore</label>
            <input
              type="color"
              value={colorToHex(formData.led_color as [number, number, number])}
              onChange={(e) => setFormData({ ...formData, led_color: hexToColor(e.target.value) })}
              className="w-full h-10 rounded cursor-pointer"
            />
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Annulla
        </button>
        <button type="submit" className="btn-primary">
          {zone ? 'Aggiorna Zona' : 'Crea Zona'}
        </button>
      </div>
    </form>
  )
}
